
import React from "react";
import { Helmet } from "react-helmet";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { CTASection } from "@/components/CTASection";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { Brackets, Shield, Smile, CheckCircle2, Crown, Settings } from "lucide-react";
import { ServiceOverviewSection } from "@/components/services/ServiceOverviewSection";
import { ServiceFeaturesSection } from "@/components/services/ServiceFeaturesSection";
import { WhyChooseUsSection } from "@/components/services/WhyChooseUsSection";
import { ServiceFAQSection } from "@/components/services/ServiceFAQSection";

const Invisalign: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Invisalign in Nagpur | Cost of Invisalign in Nagpur</title>
        <meta
          name="description"
          content="Get a straighter smile with invisible Invisalign treatment at Asrani Dental Clinic in Nagpur. Clear, comfortable aligners customized for your teeth. Schedule a consultation today!"
        />
      </Helmet>

      <Header />

      <main className="pt-20">
        <PageTitle title="Invisalign Treatment" />

        <ServiceOverviewSection 
          title="Invisalign Treatment at Asrani Dental, Sadar"
          description={
            <>
              <p className="mb-4">
                Invisalign is a revolutionary orthodontic treatment that uses clear, custom-made aligners to gradually straighten your teeth without the need for metal braces. At Asrani Dental Clinic in Nagpur, we offer advanced Invisalign treatment to give you a beautiful, confident smile with minimal disruption to your daily life.
              </p>
              <p className="mb-4">
                These virtually invisible aligners are removable, allowing you to eat, brush, and floss normally throughout your treatment. Each set of aligners is precisely designed using 3D technology to shift your teeth into their ideal position over time.
              </p>
              <p className="mb-6">
                Our experienced orthodontic team will create a personalized Invisalign treatment plan tailored specifically to your needs, ensuring optimal results for your unique smile.
              </p>
            </>
          }
          featureIcon={Brackets}
          featureTitle="Discreet Teeth Straightening"
          featureDescription="Invisalign offers a nearly invisible way to straighten your teeth, perfect for adults and teens who want effective orthodontic treatment without the look of traditional braces."
          imageSrc="/lovable-uploads/Invisalign.png"
          imageAlt="Invisalign Treatment"
        />

        <ServiceFeaturesSection 
          title="Invisalign Treatment Features"
          subtitle="Why Patients Choose Invisalign at Asrani Dental"
          features={[
            {
              icon: Brackets,
              title: "Nearly Invisible",
              description: "Invisalign aligners are made from clear, medical-grade plastic that's virtually undetectable when worn, letting you straighten your teeth discreetly."
            },
            {
              icon: Shield,
              title: "Removable Comfort",
              description: "Unlike traditional braces, Invisalign aligners can be removed when eating, drinking, brushing, and flossing, allowing for greater comfort and convenience."
            },
            {
              icon: Settings,
              title: "Custom 3D Planning",
              description: "Each treatment begins with advanced 3D imaging and planning, allowing you to see your projected results before treatment even begins."
            },
            {
              icon: CheckCircle2,
              title: "Fewer Office Visits",
              description: "Invisalign typically requires fewer and shorter dental visits than traditional braces, as no adjustments are needed—just check-ups to ensure progress."
            },
            {
              icon: Smile,
              title: "No Diet Restrictions",
              description: "With Invisalign, you can continue to enjoy all your favorite foods without worrying about damaging wires or brackets—simply remove your aligners when eating."
            },
            {
              icon: Crown,
              title: "Effective Results",
              description: "Invisalign effectively treats a wide range of orthodontic issues, from simple to complex cases, with predictable outcomes and impressive results."
            }
          ]}
        />

        <WhyChooseUsSection 
          title="Why Choose Us for Invisalign"
          subtitle="Expert Orthodontic Care at Asrani Dental, Nagpur"
          reasons={[
            {
              number: 1,
              title: "Certified Invisalign Providers",
              description: "Our dentists are certified Invisalign providers with extensive experience in planning and executing successful Invisalign treatments."
            },
            {
              number: 2,
              title: "Advanced Technology",
              description: "We use state-of-the-art digital scanning and 3D modeling technology to create precise treatment plans and custom aligners for optimal results."
            },
            {
              number: 3,
              title: "Personalized Care",
              description: "We develop individualized treatment plans tailored to your specific orthodontic needs, lifestyle, and smile goals for the best possible outcome."
            },
            {
              number: 4,
              title: "Comprehensive Support",
              description: "Our team provides ongoing support throughout your Invisalign journey, from initial consultation to final results, ensuring your treatment goes smoothly."
            }
          ]}
        />

        <ServiceTestimonials
          testimonials={[
            {
              name: "Neha Khanna",
              comment: "I was hesitant about getting braces at my age, but Invisalign at Asrani Dental was the perfect solution! The aligners are truly invisible and the process was so much easier than I expected. I'm thrilled with my new smile!",
              rating: 5,
              image: "/placeholder.svg"
            },
            {
              name: "Rahul Mehta",
              comment: "The Invisalign treatment at Asrani Dental has been life-changing. I can smile confidently now, and the best part was that nobody even noticed I was wearing aligners during my treatment. Highly recommend!",
              rating: 5,
              image: "/placeholder.svg"
            }
          ]}
          title="What Our Invisalign Patients Say"
          subtitle="Real Experiences from Patients at Asrani Dental Clinic in Nagpur"
        />

        <ServiceFAQSection 
          title="Frequently Asked Questions"
          subtitle="Common Questions About Invisalign Treatment in Nagpur"
          faqs={[
            {
              question: "How long does Invisalign treatment take?",
              answer: "Treatment time varies depending on the complexity of your case, but most Invisalign treatments take between 6-18 months. Simple cases may be completed in as little as 6 months, while more complex cases might take longer. During your consultation, we'll provide a more accurate timeline for your specific situation."
            },
            {
              question: "How much does Invisalign cost in Nagpur?",
              answer: "The cost of Invisalign treatment at Asrani Dental Clinic in Nagpur varies depending on the complexity of your case and treatment duration. We offer flexible payment plans to make Invisalign affordable, and we'll provide a detailed cost breakdown during your consultation."
            },
            {
              question: "Is Invisalign painful?",
              answer: "Invisalign is generally considered less painful than traditional braces. You may experience some temporary discomfort or pressure when you first start wearing a new set of aligners, but this typically subsides within a few days as your teeth adjust."
            },
            {
              question: "How many hours per day should I wear my Invisalign aligners?",
              answer: "For best results, you should wear your Invisalign aligners for 20-22 hours per day, removing them only for eating, drinking (except water), brushing, and flossing. The more consistently you wear your aligners, the more effective and timely your treatment will be."
            },
            {
              question: "Can Invisalign fix all orthodontic issues?",
              answer: "Invisalign can treat many orthodontic issues, including crowding, gaps, overbites, underbites, and crossbites. However, very severe or complex cases might be better suited for traditional orthodontics. During your consultation, we'll evaluate if Invisalign is the right treatment option for your specific needs."
            }
          ]}
        />

        <CTASection
          title="Start Your Invisalign Journey Today"
          description="Transform your smile discreetly with Invisalign at Asrani Dental Clinic in Nagpur."
          primaryButtonText="Book Consultation"
          primaryButtonLink="/dental-appointment-booking"
        />
      </main>

      <Footer />
    </>
  );
};

export default Invisalign;
